import { createContext, useContext, useState, useMemo, useEffect, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface EventShellTab {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface EventShellContextValue {
  tabs: EventShellTab[];
  setTabs: (tabs: EventShellTab[]) => void;
}

const EventShellContext = createContext<EventShellContextValue | null>(null);

export function EventShellProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<EventShellTab[]>([]);
  const value = useMemo(() => ({ tabs, setTabs }), [tabs]);
  return <EventShellContext.Provider value={value}>{children}</EventShellContext.Provider>;
}

export function useEventShell() {
  const ctx = useContext(EventShellContext);
  if (!ctx) throw new Error('useEventShell must be used within EventShellProvider');
  return ctx;
}

// Hook for event pages to publish their tabs into the shell's mobile bottom nav.
// Tabs are cleared on unmount so other pages don't inherit them.
export function usePublishEventTabs(tabs: EventShellTab[]) {
  const { setTabs } = useEventShell();
  const key = tabs.map((t) => t.id + ':' + t.label).join('|');
  useEffect(() => {
    setTabs(tabs);
    return () => setTabs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setTabs]);
}
